import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { config } from '../config';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useLanguage } from '../contexts/LanguageContext';

export const QueueScreen = ({ onMatched, onLeave }) => {
    const { t } = useLanguage();
    const [queueInfo, setQueueInfo] = useState({ position: 1, length: 1 });
    const [error, setError] = useState(null);
    const [isPolling, setIsPolling] = useState(true);
    const [messageIndex, setMessageIndex] = useState(0);

    const loadingMessages = t('loading_messages', { returnObjects: true }) || [];

    // Rotate loading messages
    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex(prev => (prev + 1) % loadingMessages.length);
        }, 2500);
        return () => clearInterval(interval);
    }, [loadingMessages.length]);

    useEffect(() => {
        let pollingActive = true;
        let timeoutId = null;

        const pollMatchmaking = async () => {
            if (!pollingActive) return;

            try {
                const response = await apiClient.enqueue();

                if (response.status === 'matched') {
                    // Match found haptic
                    if (window.Telegram?.WebApp?.HapticFeedback) {
                        try {
                            window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
                        } catch (e) {
                            console.error('Haptic error', e);
                        }
                    }

                    // Found game
                    onMatched({
                        gameId: response.game_id,
                        players: response.players,
                        botPlayerId: response.bot_player_id,
                    });
                    pollingActive = false; // Stop polling
                } else if (response.status === 'queued') {
                    // Update info
                    setQueueInfo({
                        position: response.position || 1,
                        length: response.queue_length || 1
                    });

                    // Continue polling
                    if (pollingActive) {
                        timeoutId = setTimeout(pollMatchmaking, config.queuePollingInterval || 2000);
                    }
                }
            } catch (err) {
                console.error('Queue polling error:', err);
                setError(err.message || t('error_title'));
                pollingActive = false;
            }
        };

        // Start polling
        pollMatchmaking();

        return () => {
            pollingActive = false;
            if (timeoutId) clearTimeout(timeoutId);
            // We do NOT call apiClient.leaveQueue() here automatically because unmount
            // might happen due to match found. Explicit leave handles the API call.
        };
    }, [onMatched, t]);

    const handleLeave = async () => {
        setIsPolling(false);
        try {
            await apiClient.leaveQueue();
        } catch (err) {
            console.error('Leave queue error:', err);
        }
        onLeave();
    };

    const handleRetry = () => {
        window.location.reload();
        // Or we could reset state to retry, but reload is safer for now as per original code
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 space-y-12 animate-fade-in relative z-10 w-full max-w-md mx-auto h-full">

            <div className="text-center space-y-8 w-full flex flex-col items-center">
                <h1 className="text-2xl font-bold text-white tracking-wider">{t('queue_title')}</h1>

                {/* Radar/Spinner animation container */}
                <div className="relative w-40 h-40 flex items-center justify-center">
                    {/* Outer static rings */}
                    <div className="absolute inset-0 border border-slate-700/30 rounded-full" />
                    <div className="absolute inset-4 border border-slate-700/50 rounded-full" />

                    {/* Rotating Radar Scanner */}
                    <div className="absolute inset-0 rounded-full animate-spin-slow active-radar-mask">
                        {/* The sweep gradient */}
                        <div className="w-full h-full rounded-full bg-[conic-gradient(from_0deg,transparent_0deg,transparent_270deg,rgba(6,182,212,0.4)_360deg)]" />
                    </div>

                    {/* Inner Circle content */}
                    <div className="w-24 h-24 bg-slate-900 rounded-full border border-cyan-500/30 flex items-center justify-center relative shadow-[0_0_30px_rgba(6,182,212,0.15)] z-10">
                        {/* Center Icon */}
                        <div className="relative z-20">
                            <span className="text-3xl animate-pulse filter drop-shadow-[0_0_8px_cyan]">📡</span>
                        </div>

                        {/* Pulsing ripples from center */}
                        <div className="absolute inset-0 rounded-full border border-cyan-500/50 animate-ping-slow" />
                        <div className="absolute inset-0 rounded-full border border-cyan-500/30 animate-ping-slow delay-300" />
                    </div>
                </div>

                {error ? (
                    <Card className="bg-red-500/10 border-red-500/30">
                        <p className="text-red-400 font-medium mb-2">⚠️ {error}</p>
                        <Button variant="danger" size="sm" onClick={handleRetry}>
                            {t('queue_retry')}
                        </Button>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-col items-center pt-2">
                            <div className="text-4xl font-black text-white tabular-nums tracking-widest drop-shadow-lg">
                                <span className="text-cyan-400">{queueInfo.position}</span>
                                <span className="text-slate-600 mx-2">/</span>
                                <span className="text-slate-400">{queueInfo.length}</span>
                            </div>
                            <p className="text-[10px] text-cyan-500/70 uppercase tracking-[0.2em] font-bold mt-1">
                                {t('queue_players')}
                            </p>
                        </div>

                        {/* Animated Loading Text */}
                        <div className="h-8 flex items-center justify-center">
                            <p className="text-sm text-slate-400 animate-pulse font-mono transition-all duration-300">
                                {loadingMessages[messageIndex]}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <Button
                variant="outline"
                className="w-full max-w-xs border-white/10 hover:bg-white/5 text-slate-400 hover:text-white"
                onClick={handleLeave}
            >
                {t('queue_cancel')}
            </Button>
        </div>
    );
};
